import React from "react";
import { Badge } from "react-bootstrap";
import {
  RawConstants,
  FILTER,
  DEFAULT_FILTER,
  DEFAULT_RAW_LIST_PAGE_SIZE,
} from "lookup/RawConstants";
import { RAW_URL_FILTER_CONFIG } from "lookup/RawListColumns";
import { DateConstants } from "lookup/DateConstants";
import { SWITCH_NUMBER_STRING, SWITCH_NUMBER_OP } from "lookup/GlobalConstants";
import { formatDateForDisplay } from "services/MomentUtils";
import { twoDecimalPlaceRounder } from "services/UtilityService";
import { createListUrlFilterUtils } from "commons/utils/queryParamUtils";

export const getInitialPagination = () => ({
  page: 0,
  pageSize: DEFAULT_RAW_LIST_PAGE_SIZE,
  sorted: RawConstants.DEFAULT_SORT,
  filtered: [FILTER.defaultBackend],
  resized: [],
  expanded: {},
});

const urlFilterUtils = createListUrlFilterUtils({
  filterConfig: RAW_URL_FILTER_CONFIG,
  staticParamKeys: ["faxProgramCode"],
  includePaginationParams: true,
  defaultPageSize: DEFAULT_RAW_LIST_PAGE_SIZE,
  defaultSort: RawConstants.DEFAULT_SORT,
  stripSortIds: ["referrerId"],
});

export const {
  getUrlFilterValues,
  getFilterValueFromUrl,
  buildPrimaryFilters,
  buildSearchFromPagination,
  stripSort: stripReferrerSort,
} = urlFilterUtils;

export const buildInitialPaginationFromUrl = (search, { shouldReset = false } = {}) =>
  urlFilterUtils.buildInitialPaginationFromUrl(search, getInitialPagination(), {
    shouldReset,
  });

export const sortedModification = (sorted = []) => {
  const sortedCopy = [...sorted];
  const hasStatusDesc = sortedCopy.some(
    (element) => element.id === "status" && element.desc
  );
  if (hasStatusDesc) {
    sortedCopy.push(RawConstants.REFFRER_SORT_ASC);
    return sortedCopy;
  }
  const hasStatusAsc = sortedCopy.some(
    (element) => element.id === "status" && !element.desc
  );
  if (hasStatusAsc) {
    sortedCopy.push(RawConstants.REFFRER_SORT_DESC);
  }
  return sortedCopy;
};

export const filterModification = (filtered = []) => {
  let filteredCopy = JSON.parse(JSON.stringify(filtered));
  filteredCopy = filteredCopy.filter((element) => element.value.length > 0);

  const switchIndex = filteredCopy.findIndex(
    (element) => element.id === SWITCH_NUMBER_STRING
  );
  if (switchIndex !== -1) {
    filteredCopy[switchIndex].operator = SWITCH_NUMBER_OP;
    filteredCopy[switchIndex].id = SWITCH_NUMBER_STRING;
  }

  const statusIndex = filteredCopy.findIndex((element) => element.id === "status");
  if (statusIndex !== -1) {
    filteredCopy[statusIndex].value = filteredCopy[statusIndex].value.map((value) =>
      typeof value === "string" ? value : value.id
    );
  }

  let defaultStatusIndex = filteredCopy.findIndex(
    (x) =>
      x.id === "status" &&
      (x.value === DEFAULT_FILTER.value ||
        x.value === RawConstants.DEFAULT_FILTER_CONST ||
        x.value.includes("Default Filters"))
  );
  if (defaultStatusIndex > -1) {
    filteredCopy[defaultStatusIndex] = FILTER.defaultBackend;
  }

  const allFiltersIndex = filteredCopy.findIndex(
    (x) => x.id === "status" && x.value.includes("All Filters")
  );
  if (allFiltersIndex > -1) {
    filteredCopy[allFiltersIndex] = FILTER.allBackend;
  }

  return filteredCopy;
};

export const isResetDisabled = (filtered, sorted) =>
  filtered.length === 1 &&
  filtered[0] === FILTER.defaultBackend &&
  sorted === RawConstants.DEFAULT_SORT;

export const renderOwnerCell = (name) => {
  if (!name) return "No Owner";
  return name;
};

export const renderStatusCell = (row) => {
  let status = row.original.status;
  if (status === RawConstants.status.CLAIMED && row.original.referrerId) {
    status = RawConstants.status.FORWARDED;
  }
  const { workflowStatus } = row.original.status;
  const forwardedFromSwim =
    workflowStatus === "PENDING_RESPONSE_REVIEW" ? (
      <Badge size="sm" bg="warning">
        Forwarded from SWIM
      </Badge>
    ) : null;
  return (
    <span>
      <span style={{ color: RawConstants.statusColors[status] }}>
        &#x25cf; &nbsp;
      </span>
      {RawConstants.statusLabels[status]} {forwardedFromSwim}
    </span>
  );
};

const renderDateCell = (date) => (
  <span style={{ width: "100%", display: "flex", justifyContent: "center" }}>
    {date}
  </span>
);

const renderEffectiveDateCell = (date, status) => (
  <span style={{ width: "100%", display: "inline-block", textAlign: "center" }}>
    <span style={{ color: DateConstants.statusColors[status] }}>
      &#x25cf; &nbsp;
    </span>
    {date}
  </span>
);

export const renderEffectiveDateCol = (date, days) => {
  if (!date) {
    return null;
  }
  const status = days >= 0 ? "ComingDue" : "PastDue";
  return renderEffectiveDateCell(formatDateForDisplay(date), status);
};

export const renderLastModifiedDateCol = (date) => {
  if (date) {
    return renderDateCell(formatDateForDisplay(date));
  }
  return <span className="center">N/A</span>;
};

export const renderSavingsCell = (savings) => (
  <span style={{ width: "100%", display: "flex", justifyContent: "center" }}>
    ${twoDecimalPlaceRounder(savings)}
  </span>
);
